import React from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { Body5, H5, Container, Page } from '@ui';
import { Header } from '@app/navigation/header';
import { Body } from '@ui';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import BackArrowButton from '@ui/buttons/BackArrowButton';
import { APP_COLOR_THEME_NAMES } from '@theme/appThemes';
import { colorThemeActions } from '@constants/actionTypes';
import { setItemToStorage, STORAGE_KEYS } from '@utils/storage';
import { exportThemeAsJSON, downloadJSON, saveColorTheme, _getThemesFromStorage } from '@utils/themes';
import { showSuccessBanner } from '@utils/application';
import { useColorTheme, useFavoriteColors } from '@hooks';
import { breakpoints } from '@constants/breakpoints';
import { ROUTES } from '@constants/routes';
import { ConfirmModal } from '@features/themeBuilder/ConfirmModal';
import { ColorEditorPanel } from '@features/themeBuilder/ColorEditorPanel';
import { PreviewPanel } from '@features/themeBuilder/PreviewPanel';
import { SavedThemesSection } from '@features/themeBuilder/SavedThemesSection';

const PageWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const HeroRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding-top: 32px;
  margin-bottom: 24px;
`;

const ActiveThemeChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 20px;
  background: ${({ theme }) => theme.themeColors.hoverComponentBackground};
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  font-size: 11px;
  color: ${({ theme }) => theme.themeColors.secondaryText};
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 20px;
  align-items: start;

  @media (max-width: ${() => breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const PreviewContainer = styled.div`
  position: relative;
`;

const DisabledOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  background: transparent;
`;

const DimmedPanel = styled.div`
  opacity: ${({ $dimmed }) => ($dimmed ? 0.45 : 1)};
  pointer-events: ${({ $dimmed }) => ($dimmed ? 'none' : 'auto')};
  transition: opacity 160ms ease;
`;

const ACTIVE_THEME_LABELS = {
  [APP_COLOR_THEME_NAMES.lightProfessional]: 'Light Professional',
  [APP_COLOR_THEME_NAMES.darkProfessional]: 'Dark Professional',
  [APP_COLOR_THEME_NAMES.custom]: 'Custom',
};

export const ThemeBuilderForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const customTheme = useSelector((state) => state.colorTheme.customTheme);
  const currentColorTheme = useColorTheme(true);
  const { favorites, addFavorite, removeFavorite } = useFavoriteColors();

  const [colorThemeName, setColorThemeName] = React.useState('');
  const [themesInStorage, setThemesInStorage] = React.useState(() => _getThemesFromStorage());
  const [showResetConfirm, setShowResetConfirm] = React.useState(false);

  const isCustomThemeEnabled = currentColorTheme === APP_COLOR_THEME_NAMES.custom;
  const activeThemeLabel = ACTIVE_THEME_LABELS[currentColorTheme] || currentColorTheme;

  const refreshSavedThemes = React.useCallback(() => {
    setThemesInStorage(_getThemesFromStorage());
  }, []);

  const handleEnableCustomTheme = () => {
    dispatch({ type: colorThemeActions.set, payload: APP_COLOR_THEME_NAMES.custom });
    setItemToStorage(STORAGE_KEYS.USING_CUSTOM_THEME, 'true');
    setItemToStorage(STORAGE_KEYS.PREVIOUS_CUSTOM_THEME, null);
  };

  const handleDisableCustomTheme = () => {
    dispatch({ type: colorThemeActions.set, payload: APP_COLOR_THEME_NAMES.lightProfessional });
    setItemToStorage(STORAGE_KEYS.USING_CUSTOM_THEME, 'false');
    setItemToStorage(STORAGE_KEYS.PREVIOUS_CUSTOM_THEME, null);
  };

  const handleSave = () => {
    const name = colorThemeName.trim();
    if (!name) return;
    saveColorTheme(name, customTheme);
    refreshSavedThemes();
    showSuccessBanner(`Theme "${name}" saved`, {}, dispatch);
  };

  const handleExportDraft = () => {
    const name = colorThemeName.trim() || 'untitled-theme';
    const { json, filename } = exportThemeAsJSON(name, customTheme);
    downloadJSON(json, filename);
    showSuccessBanner(`Exported "${name}"`, {}, dispatch);
  };

  const handleReset = () => setShowResetConfirm(true);

  const handleConfirmReset = () => {
    dispatch({ type: colorThemeActions.resetCustomColorTheme });
    setShowResetConfirm(false);
    showSuccessBanner('Theme reset to defaults', {}, dispatch);
  };

  return (
    <Page transparentBackground width={'860px'} maxWidth={'860px'}>
      <BackArrowButton onClick={() => navigate(ROUTES.home)} />

      <HeroRow>
        <Container>
          <H5 bold>Theme Builder</H5>
          <Container top={4}>
            <Body5 isLowContrast>Customize your workspace color scheme</Body5>
          </Container>
          <Container top={8}>
            <ActiveThemeChip>Active: {activeThemeLabel}</ActiveThemeChip>
          </Container>
        </Container>
        {isCustomThemeEnabled && (
          <TransparentFlatButton onClick={handleDisableCustomTheme}>Disable Custom Theme</TransparentFlatButton>
        )}
      </HeroRow>

      <TwoColumnGrid>
        <DimmedPanel $dimmed={!isCustomThemeEnabled}>
          <ColorEditorPanel
            customTheme={customTheme}
            colorThemeName={colorThemeName}
            onNameChange={setColorThemeName}
            onSave={handleSave}
            onExport={handleExportDraft}
            onReset={handleReset}
            favorites={favorites}
            addFavorite={addFavorite}
            removeFavorite={removeFavorite}
          />
        </DimmedPanel>

        <PreviewContainer>
          <DimmedPanel $dimmed={!isCustomThemeEnabled}>
            <PreviewPanel />
          </DimmedPanel>
          {!isCustomThemeEnabled && (
            <DisabledOverlay>
              <FlatButton onClick={handleEnableCustomTheme}>Enable Custom Theme</FlatButton>
            </DisabledOverlay>
          )}
        </PreviewContainer>
      </TwoColumnGrid>

      <Container top={24}>
        <SavedThemesSection themesInStorage={themesInStorage} onRefresh={refreshSavedThemes} />
      </Container>

      {showResetConfirm && (
        <ConfirmModal
          title="Reset to defaults?"
          body="This will clear all 10 color choices. Imported and saved themes are not affected."
          confirmLabel="Reset"
          onConfirm={handleConfirmReset}
          onCancel={() => setShowResetConfirm(false)}
        />
      )}
    </Page>
  );
};

export const ThemeBuilder = () => {
  return (
    <PageWrapper>
      <Header />
      <Body withFooter>
        <ThemeBuilderForm />
      </Body>
    </PageWrapper>
  );
};
